import React from 'react';
import Header from './common/Header';
import Footer from './common/Footer';
import EPGGrid from './epg/EPGGrid';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDotPlayerConfig } from '../config/AppConfig';
import spotlightService from '../services/SpotlightService';
import { useProfile } from '../contexts/ProfileContext';
import './LiveTVPage.css';

const LiveTVPage = () => {
  const { channelSlug } = useParams();
  const navigate = useNavigate();
  const { currentProfile } = useProfile();

  const [currentChannel, setCurrentChannel] = useState(null);
  const [currentProgram, setCurrentProgram] = useState(null);
  const [channels, setChannels] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [showMiniPlayer, setShowMiniPlayer] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const playerMounted = useRef(false);
  const mainPlayerRef = useRef(null);
  const mountedPlayerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const overlayTimeoutRef = useRef(null);
  const componentMounted = useRef(true);

  // Reset component state when mounting or profile changes
  useEffect(() => {
    componentMounted.current = true;

    // Reset player state when component mounts or profile changes
    playerMounted.current = false;
    setShowMiniPlayer(false);
    setShowOverlay(false);

    // Reset channel and program state when profile changes
    setCurrentChannel(null);
    setCurrentProgram(null);
    setSelectedChannelId(null);
    setSelectedProgramId(null);
    setCurrentVideoId(null);

    // Cleanup function for component unmount
    return () => {
      componentMounted.current = false;

      // Clear any pending timeouts
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
        overlayTimeoutRef.current = null;
      }

      // Unmount player if it exists
      if (mountedPlayerRef.current && mountedPlayerRef.current.unmount) {
        mountedPlayerRef.current.unmount().catch(error => {
          console.warn('⚠️ Warning during component unmount cleanup:', error);
        });
        mountedPlayerRef.current = null;
      }

      // Additional cleanup attempts for global DotPlayer
      if (window.DotPlayer) {
        try {
          if (window.DotPlayer.unmount) {
            window.DotPlayer.unmount().catch(error => {
              console.warn('⚠️ Warning during global DotPlayer cleanup:', error);
            });
          }
          if (window.DotPlayer.player && window.DotPlayer.player.unmount) {
            window.DotPlayer.player.unmount().catch(error => {
              console.warn('⚠️ Warning during DotPlayer instance cleanup:', error);
            });
          }
        } catch (error) {
          console.warn('⚠️ Warning during DotPlayer cleanup:', error);
        }
      }

      playerMounted.current = false;
    };
  }, [currentProfile?.id]); // Reset when profile changes

  // Fetch EPG data to get channels and current programs
  const fetchEPGData = async () => {
    // Don't fetch if component is unmounted
    if (!componentMounted.current) return null;

    try {
      const data = await spotlightService.getEPGData();

      // Check if component is still mounted before setting state
      if (!componentMounted.current) return null;

      // Transform channels data
      const channelsData = data.channels.map(channel => ({
        id: channel._id,
        slug: channel.slug,
        name: channel.name,
        description: channel.description || '',
        channelNumber: channel.channel_number,
        logo: channel.logo,
        poster: channel.poster,
        category: channel.category?.[0] || 'General',
        language: channel.language || 'en',
        videoId: channel.video_id,
      }));

      // Extract all programs
      const allPrograms = [];
      data.channels.forEach(channel => {
        if (channel.program && Array.isArray(channel.program)) {
          channel.program.forEach(program => {
            allPrograms.push({
              id: program.program_id,
              channelId: channel._id,
              title: program.program_title,
              description: program.program_description || 'No description available',
              startTime: program.starts_at,
              endTime: program.ends_at,
              duration: program.duration,
              episodeTitle: program.episode_title || '',
              episode: program.episode,
              season: program.season,
              part: program.part,
              language: program.program_language || 'en',
              videoId: program.video_id,
            });
          });
        }
      });

      // Calculate start time (30 minutes ago, rounded down to nearest 30 minutes)
      const now = Math.floor(Date.now() / 1000);
      const thirtyMinutesAgo = now - 30 * 60;
      // Round down to nearest 30 minutes (1800 seconds)
      const roundedStartTime = Math.floor(thirtyMinutesAgo / 1800) * 1800;

      // Exactly 12 hours from start time minus one second to ensure we don't include the last slot which we don't need
      const endTime = roundedStartTime + 12 * 60 * 60 - 1;

      // Generate time slots (30-minute intervals)
      const timeSlots = [];
      let currentSlot = roundedStartTime;
      while (currentSlot <= endTime) {
        timeSlots.push({
          id: `slot-${currentSlot}`,
          time: new Date(currentSlot * 1000).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
          timestamp: currentSlot,
        });
        currentSlot += 1800; // 30 minutes in seconds
      }

      setChannels(channelsData);
      setPrograms(allPrograms);

      // Set channel based on URL slug or default to first channel
      if (channelsData.length > 0 && !currentChannel) {
        let selectedChannel;

        if (channelSlug) {
          // Find channel by slug from URL
          selectedChannel = channelsData.find(channel => channel.slug === channelSlug);
          if (!selectedChannel) {
            // If channel slug not found, redirect to live-tv without slug and use first channel
            navigate('/live-tv', { replace: true });
            selectedChannel = channelsData[0];
          }
        } else {
          // No slug provided, use first channel
          selectedChannel = channelsData[0];
        }

        setCurrentChannel(selectedChannel);
        setSelectedChannelId(selectedChannel.id);
        setSelectedProgramId(null);
        setCurrentVideoId(selectedChannel.videoId);

        // Find current program for selected channel
        const currentProg = allPrograms.find(
          program =>
            program.channelId === selectedChannel.id &&
            program.startTime <= now &&
            program.endTime > now,
        );

        if (currentProg) {
          setCurrentProgram(currentProg);
        }
      }

      // Return the structured data for EPGGrid
      return {
        channels: channelsData,
        programs: allPrograms,
        timeSlots,
        minTime: roundedStartTime,
        maxTime: endTime,
      };
    } catch (err) {
      console.error('Failed to fetch EPG data:', err);
      return null;
    }
  };

  // State to hold EPG data
  const [epgData, setEpgData] = useState({
    channels: [],
    programs: [],
    timeSlots: [],
    minTime: Math.floor(Date.now() / 1000),
    maxTime: Math.floor(Date.now() / 1000) + 12 * 3600,
  });

  // Mount DotPlayer when video ID changes
  useEffect(() => {
    if (!currentVideoId || !componentMounted.current) return;

    const mountPlayer = async () => {
      // Check if component is still mounted before proceeding
      if (!componentMounted.current) return;

      // Unmount existing player first if it exists
      if (mountedPlayerRef.current && mountedPlayerRef.current.unmount) {
        try {
          await mountedPlayerRef.current.unmount();
          mountedPlayerRef.current = null;
          playerMounted.current = false;
        } catch (error) {
          console.warn('⚠️ Warning during player unmount:', error);
        }
      }

      // Check again if component is still mounted after unmount
      if (!componentMounted.current) return;

      if (window.DotPlayer && !playerMounted.current) {
        try {
          // Multiple attempts to ensure any existing player is unmounted
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              // Try different unmount methods
              if (window.DotPlayer.unmount) {
                await window.DotPlayer.unmount();
              }

              // Also try to unmount using the player instance if it exists
              if (window.DotPlayer.player && window.DotPlayer.player.unmount) {
                await window.DotPlayer.player.unmount();
              }

              // Small delay between attempts
              if (attempt < 2) {
                await new Promise(resolve => setTimeout(resolve, 100));
              }
            } catch (unmountError) {
              console.warn(`⚠️ Unmount attempt ${attempt + 1} failed:`, unmountError);
            }
          }

          // Additional delay before mounting
          await new Promise(resolve => setTimeout(resolve, 200));
        } catch (error) {
          console.warn('⚠️ Warning during global player unmount:', error);
        }

        playerMounted.current = true;

        try {
          // Get DotPlayer configuration from centralized config
          const playerConfig = await getDotPlayerConfig(currentVideoId, '#live-tv-player', {
            autostart: true,
            inline: true,
            controls: true,
            muted: false,
            inline: true,
            controls: true,
            skin: true,
            pip: false,
            theme: {
              fontColor: '#f2f2f2', // main icon color
              fontColorHover: '#31B37C', // icon color when hovered over
              progressSliderMain: '#31B37C', // progress slider color
              progressSliderBackground: '#B4B7BB', // progress slider background color
              controlBar: '#000000', // control bar gradient background color
            },
          });

          // Final check before mounting
          if (!componentMounted.current) {
            playerMounted.current = false;
            return;
          }

          const player = await DotPlayer.mount(playerConfig);

          // Check if component is still mounted after mounting
          if (!componentMounted.current) {
            if (player && player.unmount) {
              await player.unmount();
            }
            playerMounted.current = false;
            return;
          }

          await player.isPlayerLoaded();
          mountedPlayerRef.current = player;
        } catch (error) {
          console.error('❌ Failed to mount live TV player:', error);
          playerMounted.current = false;
        }
      } else if (
        currentVideoId &&
        window.DotPlayer &&
        mountedPlayerRef.current?.load &&
        componentMounted.current
      ) {
        try {
          mountedPlayerRef.current.load(currentVideoId);
        } catch (error) {
          console.warn('⚠️ Warning during player load:', error);
        }
      }
    };

    mountPlayer();

    // Cleanup function to unmount player when component unmounts or video changes
    return () => {
      if (mountedPlayerRef.current && mountedPlayerRef.current.unmount) {
        mountedPlayerRef.current.unmount().catch(error => {
          console.warn('⚠️ Warning during cleanup unmount:', error);
        });
        mountedPlayerRef.current = null;
        playerMounted.current = false;
      }
    };
  }, [currentVideoId]);

  // Fetch EPG data on mount and refresh when returning to page
  useEffect(() => {
    const loadEPGData = async () => {
      if (!componentMounted.current) return;

      const data = await fetchEPGData();
      if (data && componentMounted.current) {
        setEpgData(data);
      }
    };

    // Load EPG data immediately
    loadEPGData();

    // Set up interval to refresh EPG data every 5 minutes
    const refreshInterval = setInterval(() => {
      if (componentMounted.current) {
        loadEPGData();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      clearInterval(refreshInterval);
    };
  }, [channelSlug, currentProfile?.id]); // Refresh when channel slug or profile changes

  // Handle channel switching
  const switchChannel = async channel => {
    // Update URL to reflect the selected channel
    navigate(`/live-tv/${channel.slug}`, { replace: true });

    setCurrentVideoId(channel.videoId);
    setCurrentChannel(channel);
    setSelectedChannelId(channel.id);
    setSelectedProgramId(null);
    // Find current program for the selected channel
    const now = Math.floor(Date.now() / 1000);
    const currentProg = programs.find(
      program =>
        program.channelId === channel.id && program.startTime <= now && program.endTime > now,
    );
    if (currentProg) {
      setCurrentProgram(currentProg);
    }
  };

  // Handle program switching
  const switchProgram = program => {
    const channel = channels.find(c => c.id === program.channelId);
    if (channel) {
      // Update URL to reflect the selected channel
      navigate(`/live-tv/${channel.slug}`, { replace: true });

      // Switch to the channel and load its video
      setCurrentVideoId(channel.videoId);
      setCurrentChannel(channel);
      setSelectedChannelId(program.channelId);
      setSelectedProgramId(program.id);
      setCurrentProgram(program);
    }
  };

  // Handle overlay visibility with delay for both mouse and touch
  const handleMouseEnter = () => {
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = null;
    }
    setShowOverlay(true);
  };

  const handleMouseLeave = () => {
    overlayTimeoutRef.current = setTimeout(() => {
      setShowOverlay(false);
    }, 3000); // 3 second delay
  };

  const handleTouchStart = () => {
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = null;
    }
    setShowOverlay(true);
  };

  const handleTouchEnd = () => {
    overlayTimeoutRef.current = setTimeout(() => {
      setShowOverlay(false);
    }, 3000); // 3 second delay
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }
    };
  }, []);

  // Sticky mini-player scroll logic with debouncing and hysteresis
  useEffect(() => {
    let scrollTimeout;
    let isScrolling = false;

    const handleScroll = () => {
      if (!mainPlayerRef.current || !componentMounted.current) return;

      // Debounce scroll events
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        if (!componentMounted.current || !mainPlayerRef.current) return;

        const rect = mainPlayerRef.current.getBoundingClientRect();

        // Use hysteresis to prevent bouncing
        // Show mini-player when main player is 800px above viewport
        // Hide mini-player when main player is 600px above viewport (different threshold)
        if (!showMiniPlayer && rect.top < -800) {
          if (componentMounted.current) setShowMiniPlayer(true);
        } else if (showMiniPlayer && rect.top > -600) {
          if (componentMounted.current) setShowMiniPlayer(false);
        }

        isScrolling = false;
      }, 50); // 50ms debounce

      isScrolling = true;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [showMiniPlayer]); // Include showMiniPlayer in dependencies for hysteresis

  return (
    <div className="min-h-screen bg-black">
      <Header />

      <main className="container px-6 py-8 mx-auto">
        {/* Video Player Section */}
        <div className="mb-8" ref={mainPlayerRef}>
          <div
            className="relative bg-secondary rounded-2xl overflow-hidden shadow-2xl border border-[#56bdaf]/30 mb-6"
            style={{ minHeight: '400px' }}
          >
            {/* Player Container - Single container with position change */}
            <div
              ref={playerContainerRef}
              className="live-tv-player-container relative w-full aspect-video min-h-[400px] pointer-events-auto"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <div
                id="live-tv-player"
                className={`live-tv-player transition-all duration-500 ease-in-out ${
                  showMiniPlayer ? 'mini-player' : 'relative w-full h-full'
                }`}
                style={{
                  minHeight: showMiniPlayer ? '225px' : '400px',
                  maxHeight: showMiniPlayer ? '225px' : 'none',
                  maxWidth: showMiniPlayer ? '400px' : 'none',
                  width: showMiniPlayer ? '400px' : '100%',
                  height: showMiniPlayer ? '225px' : '100%',
                  // Force all child elements to respect container size
                  ...(showMiniPlayer && {
                    position: 'fixed',
                    bottom: '40px',
                    right: '40px',
                    zIndex: 50,
                  }),
                }}
              ></div>

              {/* Player Overlay with Program Info - Top-left, controlled by state */}
              {!showMiniPlayer && (
                <div
                  className={`live-tv-player-overlay absolute top-4 left-4 transition-opacity duration-300 pointer-events-none ${
                    showOverlay ? 'visible opacity-100' : 'hidden opacity-0'
                  }`}
                >
                  <div className="live-tv-player-info bg-gradient-to-r from-[#1a1a1a]/90 to-[#1a1a1a]/70 backdrop-blur-sm rounded-xl p-4 pointer-events-auto max-w-md">
                    <div className="live-tv-player-header flex items-center space-x-3 mb-3">
                      <div className="live-tv-player-icon p-2 bg-[#56bdaf] rounded-lg shadow-lg">
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <h1 className="live-tv-player-title text-xl font-bold text-white">
                        {currentProgram?.title || currentChannel?.name || 'Live TV'}
                      </h1>
                    </div>

                    {/* Live indicator and channel info */}
                    <div className="live-tv-player-status flex items-center space-x-3">
                      <div className="live-tv-player-live-badge flex items-center space-x-2 bg-[#56bdaf]/20 border border-[#56bdaf]/50 rounded-full px-3 py-1">
                        <div className="live-tv-player-live-dot w-2 h-2 bg-[#56bdaf] rounded-full animate-pulse"></div>
                        <span className="live-tv-player-live-text text-[#56bdaf] text-sm font-medium">
                          LIVE
                        </span>
                      </div>
                      {currentChannel && (
                        <div className="live-tv-player-channel-badge flex items-center space-x-2 bg-secondary/80 border border-[#56bdaf]/30 rounded-full px-3 py-1">
                          <span className="live-tv-player-channel-text text-white/80 text-sm">
                            {currentChannel.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Mini Player Close Button */}
              {showMiniPlayer && (
                <button
                  className="absolute top-2 right-2 text-white/70 hover:text-white text-xl px-2 bg-[#56bdaf]/80 hover:bg-[#56bdaf] rounded-full z-10"
                  onClick={() => {
                    setShowMiniPlayer(false);
                    if (mainPlayerRef.current) {
                      mainPlayerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                  aria-label="Close mini player"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>

        {/* EPG Grid Component */}
        <div className="bg-secondary/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-2xl border border-[#56bdaf]/30">
          <EPGGrid
            data={epgData}
            onChannelSelect={switchChannel}
            onProgramSelect={switchProgram}
            selectedChannelId={selectedChannelId}
            selectedProgramId={selectedProgramId}
            fetchEPGData={fetchEPGData}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LiveTVPage;
